<template>
  <b-card
    v-if="data"
    no-body
    class="card-ticketbymerchant-budget h-100 overflow-visible"
  >
    <b-row class="mx-2">
      <b-col
        md="12"
        class="ticketbymerchant-report-wrapper"
      >
        <div
          class="d-flex flex-wrap justify-content-between align-items-center mb-3 mt-2 position-relative"
          style="z-index: 10; gap: 10px;"
        >

          <!-- Title -->
          <h4 class="card-title mb-0 chart-title d-flex align-items-center flex-wrap">
            <span class="mr-1">{{ $t(title) }}</span>
            <tooltip-help :text="$t('Merchant Tickets Breakdown')" />
          </h4>

          <!-- Buttons -->
          <div class="d-flex align-items-center flex-wrap gap-2">

            <v-select
              v-model="perPage"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="pageOptions"
              :clearable="false"
              :searchable="false"
              class="per-page-selector"
            />

            <b-button
              v-ripple.400="'rgba(113, 102, 240, 0.15)'"
              size="sm"
              variant="outline-primary"
              class="widgetrefresh-button d-flex align-items-center justify-content-center shadow-custom ml-1"
              :style="cardBorder"
              :disabled="!canRefresh"
              @click.stop="refreshWidget"
            >
              <feather-icon
                icon="RefreshCwIcon"
                class="mr-50 refresh-icon"
              />
              <time-ago
                class="timeAgo"
                :datetime="lastRefreshTime ? new Date(parseInt(lastRefreshTime, 10)).toISOString() : lastUpdatedAt"
                refresh
                locale="en"
                :long="true"
                :style="{ color: '#1C1F32' }"
              />
            </b-button>

          </div>
        </div>

        <!-- chart -->
        <vue-apex-charts
          id="ticketbymerchant-report-chart"
          type="bar"
          height="300"
          :options="chartOptions"
          :series="data.series"
        />
      </b-col>
    </b-row>
  </b-card>
</template>

<script>
import {
  BCard, BRow, BCol, BButton,
} from 'bootstrap-vue'
import VueApexCharts from 'vue-apexcharts'
import Ripple from 'vue-ripple-directive'
import { TimeAgo } from 'vue2-timeago'
import RefreshMixin from '@/@core/mixins/ui/refresh'
import vSelect from 'vue-select'
import TooltipHelp from '../Dashboard/TooltipHelp.vue'

export default {
  components: {
    VueApexCharts,
    BCard,
    BRow,
    BCol,
    TimeAgo,
    BButton,
    vSelect,
    TooltipHelp,
  },
  directives: {
    Ripple,
  },
  mixins: [RefreshMixin],
  props: {
    title: {
      type: String,
      default: null,
    },
    data: {
      type: Object,
      default: () => {},
    },
    lastUpdatedAt: {
      type: String,
      default: null,
    },
    queue: {
      type: String,
      required: true,
    },
    chartColor: {
      type: String,
      default: null, // Default to null so we can check if it was passed
    },
  },
  data() {
    return {
      perPage: { label: 'Top 10', value: 10 },
      pageOptions: [
        { label: 'Top 10', value: 10 },
        { label: 'Top 20', value: 20 },
        { label: 'Top 50', value: 50 },
      ],
      lastRefreshTime: (JSON.parse(localStorage.getItem('ticketDashboardLastUpdatedAt')) || {})[this.title] || 0,
      now: Date.now(),
      timer: null,
    }
  },
  computed: {
    chartOptions() {
      return {
        chart: {
          height: 350,
          type: 'bar',
          zoom: { enabled: false },
          toolbar: {
            export: {
              csv: {
                filename: this.title || 'Ticket_By_Merchant',
              },
              svg: {
                filename: this.title || 'Ticket_By_Merchant',
              },
              png: {
                filename: this.title || 'Ticket_By_Merchant',
              },
            },
          },
        },
        colors: this.chartColor ? [this.chartColor] : ['#0094FF'],
        fill: {
          type: 'gradient',
          gradient: {
            shade: 'dark',
            type: 'vertical',
            shadeIntensity: 0.5,
            gradientToColors: ['#B265FF'],
            inverseColors: false,
            opacityFrom: 1,
            opacityTo: 1,
            stops: [0, 100],
          },
        },
        dataLabels: { enabled: false },
        stroke: { show: false },
        grid: {
          borderColor: '#ebe9f1',
          strokeDashArray: 5,
          xaxis: { lines: { show: false } },
          yaxis: { lines: { show: true } },
          padding: {
            bottom: 25,
          },
        },
        xaxis: {
          categories: this.data?.categories?.lables || [],
          axisBorder: { show: false },
          axisTicks: { show: false },
          tooltip: {
            enabled: false,
          },
          labels: {
            show: true,
            rotate: -45,
            rotateAlways: true,
            trim: true,
            minHeight: 70,
            style: {
              fontSize: '11px',
              colors: '#b9b9c3',
              fontWeight: 500,
            },
          },
        },
        yaxis: {
          labels: {
            style: {
              colors: '#b9b9c3',
              fontSize: '11px',
            },
            formatter: val => Math.round(val).toLocaleString(),
          },
        },
        tooltip: this.tooltipConfig,
        plotOptions: this.plotOptions,
      }
    },
    tooltipConfig() {
      return {
        enabled: true,
        theme: 'dark',
        custom: ({
          series, seriesIndex, dataPointIndex,
        }) => {
          if (dataPointIndex === undefined || !this.data?.categories?.lables) return ''
          const value = series[seriesIndex][dataPointIndex]
          const name = this.data.categories.lables[dataPointIndex] || ''
          return `
            <div style="
              background: #0094FF;
              color: #fff;
              padding: 6px 12px;
              border-radius: 12px;
              font-size: 13px;
              font-weight: 600;
              position: relative;
              box-shadow: 0 4px 8px rgba(0,0,0,0.2);
              pointer-events: none;
              white-space: normal !important;
            ">
              <div style="font-size: 11px; opacity: 0.9; font-weight: 400; margin-bottom: 4px; border-bottom: 1px solid rgba(255,255,255,0.2); padding-bottom: 2px; white-space: normal !important;">${name}</div>
              <div>${value.toLocaleString()}</div>
            </div>`
        },
      }
    },
    plotOptions() {
      const totalBars = this.data?.series?.[0]?.data?.length || 0
      let columnWidth = '25%'
      if (totalBars <= 1) columnWidth = '10%'
      else if (totalBars <= 5) columnWidth = '25px'
      else if (totalBars <= 10) columnWidth = '18px'
      else columnWidth = '14px'

      return {
        bar: {
          columnWidth,
          borderRadius: 3,
          borderRadiusApplication: 'end',
        },
      }
    },
    cardBorder() {
      return {
        border: '1px solid var(--primary) !important',
        opacity: this.canRefresh ? 1 : 0.5,
      }
    },
    canRefresh() {
      const diff = this.now - parseInt(this.lastRefreshTime, 10)
      return diff >= 60000
    },
  },
  watch: {
    perPage(val) {
      this.$emit('refresh', { queue: this.queue, limit: val.value })
    },
  },
  created() {
    this.timer = setInterval(() => {
      this.now = Date.now()
    }, 1000)
  },
  beforeDestroy() {
    clearInterval(this.timer)
  },
  mounted() {
    const timestamps = JSON.parse(localStorage.getItem('ticketDashboardLastUpdatedAt')) || {}
    if (!timestamps[this.title]) {
      this.refreshWidget()
    }
  },
  methods: {
    refreshWidget() {
      if (!this.canRefresh) return
      const timestamp = Date.now()
      this.lastRefreshTime = timestamp
      const timestampsObj = JSON.parse(localStorage.getItem('ticketDashboardLastUpdatedAt')) || {}
      timestampsObj[this.title] = timestamp
      localStorage.setItem('ticketDashboardLastUpdatedAt', JSON.stringify(timestampsObj))
      this.$emit('refresh', { queue: this.queue, limit: this.perPage.value })
    },
  },
}
</script>
<style lang="scss" scoped>
@import '../@core/scss/vue/pages/widget-refresh.scss';
.per-page-selector {
  width: 120px;
  ::v-deep .vs__dropdown-toggle {
    background-color: #f3f2f7;
    border: none;
    border-radius: 8px;
    padding: 0px 4px;
    height: 38px;
    cursor: pointer;
  }
  ::v-deep .vs__selected {
    color: #5e5873;
    font-weight: 500;
    margin-top: 4px;
    font-size: 14px;
  }
  ::v-deep .vs__open-indicator {
    fill: #5e5873;
    transform: scale(0.7);
  }
  ::v-deep .vs__search {
    opacity: 0;
    position: absolute;
  }
}
.widgetrefresh-button {
  cursor: pointer;
}
.overflow-visible {
  overflow: visible !important;
}
::v-deep .apexcharts-tooltip {
  background: transparent !important;
  border: none !important;
  box-shadow: none !important;
}
</style>
<style lang="scss">
.blue-tooltip .tooltip-inner {
  background-color: #0094FF !important;
  color: #fff !important;
  font-weight: 500;
  border-radius: 8px;
  padding: 6px 12px;
}
.blue-tooltip .arrow::before {
  border-top-color: #0094FF !important;
  border-bottom-color: #0094FF !important;
}
</style>
